import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Container,
  Group,
  List,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { EntriesEditor, LogoPaper, ManifestForm, ProseEditor, VersionHistory } from '@/components';
import { formatWhen, MODE_CHIP } from '@/components/Playbooks/format';
import { usePlaybook } from '@/hooks/usePlaybooks';
import { ApiConflictError, ApiValidationError, vnextApiClient } from '@/services/vnext-api';
import {
  entriesOf,
  type PlaybookDocument,
  type PlaybookEntry,
  type PlaybookManifest,
  type PlaybookVersion,
} from '@/types/playbook';
import { toError } from '@/utils/errors';

/** What the editor holds: the manifest, the instructions and the entries
 * of the version it started from. */
interface Draft {
  from: number;
  manifest: PlaybookManifest;
  instructions: string;
  entries: PlaybookEntry[];
}

function draftOf(version: PlaybookVersion): Draft {
  return {
    from: version.number,
    manifest: version.manifest,
    instructions: version.instructions,
    entries: entriesOf(version),
  };
}

function same(a: Draft, b: Draft): boolean {
  return (
    JSON.stringify([a.manifest, a.instructions, a.entries]) ===
    JSON.stringify([b.manifest, b.instructions, b.entries])
  );
}

/**
 * One playbook: its current version — the manifest, the instructions and
 * the documents' index entries, edited here and published as the next
 * version with a reason — and its history, each version readable. The house
 * is read here only: it is seeded from the repository.
 */
export function PlaybookPage() {
  const { key = '' } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { data: detail, error, isLoading, refetch } = usePlaybook(key);
  const current = detail?.current ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[] | null>(null);

  // The editor starts from the current version, and again when a new one
  // is published (here or elsewhere) — never over edits in hand.
  useEffect(() => {
    if (current && (draft === null || draft.from !== current.number)) {
      setDraft(draftOf(current));
    }
  }, [current, draft]);

  const base = useMemo(() => (current ? draftOf(current) : null), [current]);
  const changed = draft !== null && base !== null && !same(draft, base);

  if (isLoading && !detail) {
    return (
      <Center h={300}>
        <Loader />
      </Center>
    );
  }
  if (!detail) {
    return (
      <Container size="xl" py="md">
        <Text c="dimmed">{error ? 'Not found.' : 'Loading…'}</Text>
      </Container>
    );
  }

  const { summary } = detail;
  const house = summary.kind === 'house';
  const mode = summary.mode ? MODE_CHIP[summary.mode] : null;

  const openFile = async (document: PlaybookDocument) => {
    if (!document.fileId) {
      return;
    }
    try {
      const link = await vnextApiClient.getPlaybookFileLink(key, document.fileId);
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({ color: 'red', message: toError(err, 'Not opened').message });
    }
  };

  const publish = async () => {
    if (!draft || !reason.trim()) {
      return;
    }
    setBusy(true);
    setProblems(null);
    try {
      await vnextApiClient.publishPlaybook(key, {
        manifest: draft.manifest,
        instructions: draft.instructions,
        entries: draft.entries,
        reason: reason.trim(),
      });
      notifications.show({ color: 'teal', message: 'Published as the next version.' });
      setPublishing(false);
      setReason('');
      await refetch();
    } catch (err) {
      if (err instanceof ApiValidationError || err instanceof ApiConflictError) {
        const listed = Array.isArray(err.detail.problems) ? (err.detail.problems as string[]) : [];
        setProblems(listed.length > 0 ? listed : [err.message]);
      } else {
        Sentry.captureException(toError(err));
        setProblems([toError(err, 'Not published').message]);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack>
        <Anchor size="sm" onClick={() => navigate('/admin/playbooks')}>
          ← Playbooks
        </Anchor>
        <Group justify="space-between" align="flex-start">
          <Stack gap={2}>
            <Group gap="xs">
              <Title order={2}>{summary.name}</Title>
              {house ? (
                <Badge variant="light">House</Badge>
              ) : mode ? (
                <Badge color={mode.color} variant={mode.variant}>
                  {mode.label}
                </Badge>
              ) : null}
            </Group>
            <Text size="sm" c="dimmed">
              {summary.key}
              {summary.clientCode ? ` · ${summary.clientCode}` : ''}
              {summary.teams.length ? ` · teams ${summary.teams.join(', ')}` : ''}
              {current
                ? ` · v${current.number}, published ${formatWhen(current.publishedAt)}${
                    current.publishedBy ? ` by ${current.publishedBy}` : ''
                  }${current.houseVersion ? ` · over house v${current.houseVersion}` : ''}`
                : ' · not published yet'}
            </Text>
            {current?.changeReason ? <Text size="sm">“{current.changeReason}”</Text> : null}
          </Stack>
          {house ? (
            <Text size="sm" c="dimmed" maw={320}>
              The house is changed in the repository (content/house) and seeded; it is read here.
            </Text>
          ) : (
            <Group gap="xs">
              <Button
                variant="default"
                disabled={!changed || busy}
                onClick={() => base && setDraft(base)}
                data-testid="playbook-discard"
              >
                Discard changes
              </Button>
              <Button
                disabled={!changed || !current}
                onClick={() => setPublishing(true)}
                data-testid="playbook-publish"
              >
                Publish…
              </Button>
            </Group>
          )}
        </Group>

        {!current ? (
          <Alert color="orange" variant="light">
            No version is published yet: publish the folder with the seed command first.
          </Alert>
        ) : draft ? (
          <Stack>
            <LogoPaper>
              <Stack>
                <Title order={4}>The manifest</Title>
                <ManifestForm
                  manifest={draft.manifest}
                  onChange={(manifest) => setDraft({ ...draft, manifest })}
                  readOnly={house}
                />
              </Stack>
            </LogoPaper>
            <LogoPaper>
              <Stack>
                <Title order={4}>The instructions</Title>
                <Text size="xs" c="dimmed">
                  The procedure the case agent follows, in prose: what to read, in what order, what
                  to establish, when to ask a person. Code keeps the guards; the order is this text.
                </Text>
                <ProseEditor
                  label="Instructions"
                  value={draft.instructions}
                  onChange={(instructions) => setDraft({ ...draft, instructions })}
                  minRows={12}
                />
              </Stack>
            </LogoPaper>
            <LogoPaper>
              <Stack>
                <Title order={4}>The documents</Title>
                <Text size="xs" c="dimmed">
                  Each file in the folder with how the agents find it: its kind, the report types it
                  applies to, and the description they read it by. The files themselves stay as
                  published; a changed file is a new folder publish from the command line.
                </Text>
                <EntriesEditor
                  documents={current.documents}
                  entries={draft.entries}
                  onChange={(entries) => setDraft({ ...draft, entries })}
                  readOnly={house}
                  onOpenFile={(document) => void openFile(document)}
                />
              </Stack>
            </LogoPaper>
          </Stack>
        ) : null}

        <LogoPaper>
          <Stack>
            <Title order={4}>History</Title>
            <VersionHistory playbookKey={key} versions={detail.history} />
          </Stack>
        </LogoPaper>
      </Stack>

      <Modal
        opened={publishing}
        onClose={() => setPublishing(false)}
        title="Publish the next version?"
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            The manifest, the instructions and the entries as they stand here become version{' '}
            {current ? current.number + 1 : ''}. Cases already running keep the version they started
            with; new cases take this one.
          </Text>
          <Textarea
            label="Why"
            placeholder="Recorded on the version"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            autosize
            minRows={2}
            required
            data-testid="publish-reason"
          />
          {problems ? (
            <Alert color="red" variant="light" data-testid="publish-problems">
              <List size="sm">
                {problems.map((problem) => (
                  <List.Item key={problem}>{problem}</List.Item>
                ))}
              </List>
            </Alert>
          ) : null}
          <Group justify="flex-end" gap="xs">
            <Button size="xs" variant="subtle" onClick={() => setPublishing(false)}>
              Cancel
            </Button>
            <Button
              size="xs"
              disabled={!reason.trim()}
              loading={busy}
              onClick={() => void publish()}
              data-testid="publish-confirm"
            >
              Publish
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
